import LowPriorityIcon from '@material-ui/icons/ArrowDownward';
import HighPriorityIcon from '@material-ui/icons/ArrowUpward';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import ErrorIcon from '@material-ui/icons/Error';
import StatusCircleIcon from '@material-ui/icons/FiberManualRecord';
import AllPrioritiesIcon from '@material-ui/icons/FilterList';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import NormalPriorityIcon from '@material-ui/icons/ReportProblemOutlined';
import { ReactComponent as InProgressIcon } from 'assets/wg-well-status-inprogress.svg';
import { ReactComponent as StoppedIcon } from 'assets/wg-well-status-stopped.svg';
import {
  AlertPriorities,
  FINAL_RECOMMENDATION_TYPES,
  WELL_STATUS,
  WELL_STATUS_DESCS,
  WELL_STATUS_UPDATE,
} from 'types/wellGuardian';

import styles from '../WgChip.module.scss';

export interface ChipConfig {
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  labelTranslationKey?: string;
  text?: string;
  className?: string;
}

export function getChipConfigFromPriority(priority: AlertPriorities | undefined): ChipConfig {
  let chipConfig: ChipConfig;

  switch (priority) {
    case AlertPriorities.ALL:
      chipConfig = {
        startIcon: <AllPrioritiesIcon fontSize="inherit" />,
        labelTranslationKey: 'wellGuardian.wellSingle.alerts.priorities.all',
      };
      break;

    case AlertPriorities.HIGH:
      chipConfig = {
        startIcon: <HighPriorityIcon fontSize="inherit" />,
        labelTranslationKey: 'wellGuardian.wellSingle.alerts.priorities.high',
        className: styles.priorityHigh,
      };
      break;

    case AlertPriorities.NORMAL:
      chipConfig = {
        startIcon: <NormalPriorityIcon fontSize="inherit" />,
        labelTranslationKey: 'wellGuardian.wellSingle.alerts.priorities.normal',
        className: styles.priorityNormal,
      };
      break;

    case AlertPriorities.LOW:
      chipConfig = {
        startIcon: <LowPriorityIcon fontSize="inherit" />,
        labelTranslationKey: 'wellGuardian.wellSingle.alerts.priorities.low',
        className: styles.priorityLow,
      };
      break;

    default:
      chipConfig = {
        labelTranslationKey: 'wellGuardian.wellSingle.alerts.priorities.default',
      };
  }

  return chipConfig;
}

enum SynergiRiskLevels {
  GREEN = 'green',
  AMBER = 'amber',
  RED = 'red',
}

export function getChipConfigFromSynergiLevelAndValue(
  initialRiskLevel?: string,
  riskValue?: string,
): ChipConfig {
  const chipConfig: ChipConfig = { text: riskValue };

  // Initial risk level format: N. Risk Level N (color)
  const riskLevelColor = initialRiskLevel?.substring(
    initialRiskLevel?.indexOf('(') + 1,
    initialRiskLevel?.indexOf(')'),
  );

  switch (riskLevelColor) {
    case SynergiRiskLevels.GREEN:
      chipConfig.className = styles.synergiGreen;
      break;

    case SynergiRiskLevels.AMBER:
      chipConfig.className = styles.synergiAmber;
      break;

    case SynergiRiskLevels.RED:
      chipConfig.className = styles.synergiRed;
      break;

    default:
      chipConfig.className = styles.synergiTransparent;
      break;
  }

  return chipConfig;
}

export function getChipConfigFromWellStatus(status?: string) {
  const chipConfig: ChipConfig = { text: status ?? 'Unknown' };

  switch (status) {
    case WELL_STATUS.COMPLETED_SHUT_IN:
    case WELL_STATUS.COMPLETED_SHUT_IN_:
    case WELL_STATUS.COMPLETED_CYCLE:
    case WELL_STATUS.ABANDONNED_PHASE_1:
    case WELL_STATUS.ABANDONNED_PHASE_2:
    case WELL_STATUS.PLUGGED:
    case WELL_STATUS.SUSPENDED:
    case WELL_STATUS.SUSPENDED_COMPLETED:
    case WELL_STATUS.SUSPENDED_COMPLETED_SHUT_IN:
    case WELL_STATUS.SUSPENDED_SHUT_IN:
    case WELL_STATUS.TEMPORARY_ABANDONED:
    case WELL_STATUS.TEMPORARY_ABANDONED_SHUT_IN:
    case WELL_STATUS.TEMPORARY_ABANDONED_AB1:
    case WELL_STATUS.TEMPORARY_ABANDONED_AB2:
    case WELL_STATUS.PERMANENTLY_ABANDONED:
    case WELL_STATUS.PERMANENTLY_ABANDONED_SHUT_IN:
    case WELL_STATUS.PROGRESS:
      chipConfig.className = styles.statusOrange;
      chipConfig.endIcon = <StoppedIcon />;
      break;

    case WELL_STATUS.ABANDONNED_PHASE_3:
      chipConfig.className = styles.statusRed;
      chipConfig.endIcon = <ErrorIcon />;
      break;

    default:
      chipConfig.className = styles.statusGreen;
      chipConfig.endIcon = <InProgressIcon />;
      break;
  }

  return chipConfig;
}

export function getChipConfigFromFinalRecommendation(status?: string) {
  const chipConfig: ChipConfig = { text: status };

  switch (status) {
    case FINAL_RECOMMENDATION_TYPES.NOT_RECOMMENDED:
      chipConfig.className = styles.notRecommended;
      chipConfig.startIcon = <RemoveCircleOutlineIcon fontSize="inherit" />;
      break;

    case FINAL_RECOMMENDATION_TYPES.RECOMMENDED:
      chipConfig.className = styles.recommended;
      chipConfig.startIcon = <CheckIcon fontSize="inherit" />;
      break;

    case FINAL_RECOMMENDATION_TYPES.CANCELED:
      chipConfig.className = styles.canceled;
      chipConfig.startIcon = <ClearIcon fontSize="inherit" />;
      break;

    default:
      chipConfig.className = styles.notAvailable;
      chipConfig.labelTranslationKey =
        'wellGuardian.wellSingle.overview.activeDerogations.notAvailable';
      break;
  }

  return chipConfig;
}

export const getChipConfigFromWellStatusDesc = (wellStatusDesc?: string | null) => {
  const additionStatus =
    wellStatusDesc === WELL_STATUS_DESCS.HEALTHY
      ? WELL_STATUS_UPDATE.HEALTHY
      : wellStatusDesc === WELL_STATUS_DESCS.MINOR_MEASURES
      ? WELL_STATUS_UPDATE.MINOR_MEASURES
      : wellStatusDesc === WELL_STATUS_DESCS.MAJOR_MEASURES
      ? WELL_STATUS_UPDATE.MAJOR_MEASURES
      : wellStatusDesc === WELL_STATUS_DESCS.NOT_ACCEPTABLE
      ? WELL_STATUS_UPDATE.NOT_ACCEPTABLE
      : wellStatusDesc;

  const chipConfig: ChipConfig = { text: additionStatus ?? '' };

  switch (additionStatus) {
    case WELL_STATUS_UPDATE.HEALTHY:
      chipConfig.className = '#00c85c';
      break;
    case WELL_STATUS_UPDATE.MINOR_MEASURES:
      chipConfig.className = '#ebe376';
      break;
    case WELL_STATUS_UPDATE.MAJOR_MEASURES:
      chipConfig.className = '#ecaa00';
      break;
    case WELL_STATUS_UPDATE.NOT_ACCEPTABLE:
      chipConfig.className = '#ff5b64';
      break;
    default:
      chipConfig.className = '#808080';
  }

  return chipConfig;
};

export const getChipConfigIntegrityDetails = (integrityValue?: string) => {
  const additionStatus =
    integrityValue === 'Pass'
      ? 'PASS'
      : integrityValue === 'Fail'
      ? 'FAIL'
      : integrityValue === 'Warning'
      ? 'WARNING'
      : integrityValue === null
      ? 'EMPTY'
      : integrityValue;

  const chipConfig: ChipConfig = { text: additionStatus ?? '' };

  switch (additionStatus) {
    case 'PASS':
      chipConfig.className = '#00c85c';
      break;
    case 'FAIL':
      chipConfig.className = '#ff5b64';
      break;
    case 'WARNING':
      chipConfig.className = '#ecaa00';
      break;
    case 'EMPTY':
      chipConfig.className = '#10274133';
      break;
    default:
      chipConfig.className = '';
  }

  return chipConfig;
};

export function getChipConfigFromWellOnlineStatus(status: string) {
  const chipConfig: ChipConfig = { text: status };

  switch (status) {
    case '0':
      chipConfig.className = styles.wellStatusOffline;
      chipConfig.startIcon = <StatusCircleIcon fontSize="inherit" />;
      chipConfig.text = undefined;
      chipConfig.labelTranslationKey = 'wellGuardian.allWells.offlineStatus';
      break;

    case '1':
      chipConfig.className = styles.wellStatusOnline;
      chipConfig.startIcon = <StatusCircleIcon fontSize="inherit" />;
      chipConfig.text = undefined;
      chipConfig.labelTranslationKey = 'wellGuardian.allWells.onlineStatus';
      break;

    default:
      chipConfig.className = styles.wellStatusUnknown;
      chipConfig.startIcon = <StatusCircleIcon fontSize="inherit" />;
      chipConfig.text = undefined;
      chipConfig.labelTranslationKey = 'wellGuardian.allWells.Unknown';
      break;
  }

  return chipConfig;
}
